// Body + attachment metadata as React Query queries, shared by every email surface.
// Cached per (mailbox, messageId) so re-expanding a row or re-selecting in the pop-out
// doesn't refetch. NOTE: the body fetch is the call that marks the message read
// server-side (W4) — callers pair it with actions.markReadLocal for the optimistic dot.

import { useQuery } from "@tanstack/react-query";
import { emailApi, fetchWithAuth } from "@/lib/api";

export interface EmailAttachmentMeta {
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline?: boolean;
}

export function useEmailBody(mailbox: string, messageId: string, opts: { enabled: boolean; fallback?: string | null }) {
  return useQuery<{ content: string; contentType: string }>({
    queryKey: ["email-body", mailbox, messageId],
    // Failures THROW (no catch-to-fallback) so a transient Graph blip isn't cached as a
    // "successful" body for 5 minutes — react-query retries, and callers render the
    // snippet on isError. Successes cache normally.
    queryFn: async () => {
      const full = await emailApi.getEmailBody(mailbox, messageId);
      return full.body ?? { content: opts.fallback ?? "", contentType: "text" };
    },
    enabled: opts.enabled,
    retry: 1,
    staleTime: 5 * 60_000,
  });
}

export function useEmailAttachments(mailbox: string, messageId: string, opts: { enabled: boolean }) {
  return useQuery<EmailAttachmentMeta[]>({
    queryKey: ["email-attachments", mailbox, messageId],
    queryFn: () =>
      fetchWithAuth<EmailAttachmentMeta[]>(
        `/api/emails/message-attachments?mailbox=${encodeURIComponent(mailbox)}&messageId=${encodeURIComponent(messageId)}`,
      ),
    enabled: opts.enabled,
    staleTime: 5 * 60_000,
  });
}

/** Fetch an attachment's bytes with auth and hand back a blob URL. */
export async function fetchAttachmentBlobUrl(
  mailbox: string,
  messageId: string,
  attachmentId: string,
  filename: string,
): Promise<string> {
  const { getSupabase } = await import("@/lib/supabase");
  const supabase = await getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  const url = `/api/emails/message-attachments/download?mailbox=${encodeURIComponent(mailbox)}&messageId=${encodeURIComponent(messageId)}&attachmentId=${encodeURIComponent(attachmentId)}&filename=${encodeURIComponent(filename)}`;
  const resp = await fetch(url, {
    headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}
